import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { getZonePhotoUrl } from '../../api';
import { useDeleteZonePhoto, useUploadZonePhoto, useZone } from '../../hooks/useWmsLocations';
import type { Zone, ZoneType, CreateZoneInput, UpdateZoneInput } from '../../types';

const zoneTypes: { value: ZoneType; label: string }[] = [
  { value: 'expedition', label: 'Expedition' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'production', label: 'Production' },
  { value: 'quarantine', label: 'Quarantine' },
  { value: 'calibration', label: 'Calibration' },
  { value: 'staging', label: 'Staging' },
  { value: 'returns', label: 'Returns' },
];

const defaultColors = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

interface ZoneFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zone?: Zone | null;
  onSubmit: (data: CreateZoneInput | UpdateZoneInput) => Promise<void>;
  isLoading?: boolean;
}

export function ZoneFormModal({
  open,
  onOpenChange,
  zone,
  onSubmit,
  isLoading,
}: ZoneFormModalProps) {
  const isEditing = !!zone;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const liveZoneQuery = useZone(zone?.id || '');
  const uploadPhotoMutation = useUploadZonePhoto();
  const deletePhotoMutation = useDeleteZonePhoto();

  const [formData, setFormData] = useState<CreateZoneInput>({
    code: '',
    name: '',
    zone_type: 'inventory',
    color: defaultColors[0],
    description: '',
    pick_priority: 100,
  });
  const [photoPreviewError, setPhotoPreviewError] = useState(false);

  useEffect(() => {
    if (zone) {
      setFormData({
        code: zone.code,
        name: zone.name,
        zone_type: zone.zone_type,
        color: zone.color || defaultColors[0],
        description: zone.description || '',
        pick_priority: zone.pick_priority ?? 100,
      });
    } else {
      setFormData({
        code: '',
        name: '',
        zone_type: 'inventory',
        color: defaultColors[0],
        description: '',
        pick_priority: 100,
      });
    }
  }, [zone, open]);

  const liveZone = liveZoneQuery.data;
  const currentZone = liveZone || zone || null;
  const currentPhotoPath = currentZone?.photo_storage_path || null;
  const isPhotoBusy = uploadPhotoMutation.isPending || deletePhotoMutation.isPending;

  useEffect(() => {
    setPhotoPreviewError(false);
  }, [currentPhotoPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { code, ...updateData } = formData;
        await onSubmit(updateData);
        toast.success('Zone updated successfully');
      } else {
        await onSubmit(formData);
        toast.success('Zone created successfully');
      }
      onOpenChange(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} zone: ${message}`);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} zone:`, error);
      // Keep form open for retry
    }
  };

  const handlePhotoSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentZone) {
      event.target.value = '';
      return;
    }

    try {
      await uploadPhotoMutation.mutateAsync({ zoneId: currentZone.id, file });
    } finally {
      event.target.value = '';
    }
  };

  const handleDeletePhoto = async () => {
    if (!currentZone) return;
    await deletePhotoMutation.mutateAsync(currentZone.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Zone' : 'Create Zone'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the zone details below.'
              : 'Add a new workshop zone for organizing locations.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  placeholder="e.g., D, E, F"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={isEditing}
                  maxLength={10}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zone_type">Type</Label>
                <Select
                  value={formData.zone_type}
                  onValueChange={(v) => setFormData({ ...formData, zone_type: v as ZoneType })}
                >
                  <SelectTrigger id="zone_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {zoneTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="e.g., Expedition Area, Inventory Storage"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Color</Label>
              <div className="flex gap-2 flex-wrap">
                {defaultColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      formData.color === color
                        ? 'border-foreground scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-8 h-8 p-0 border-0 cursor-pointer"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pick_priority">Pick Priority</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="pick_priority"
                  type="number"
                  min={1}
                  max={999}
                  value={formData.pick_priority}
                  onChange={(e) => setFormData({ ...formData, pick_priority: parseInt(e.target.value, 10) || 100 })}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">
                  Lower numbers are picked first
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Additional details about this zone..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              <div className="space-y-1">
                <Label>Zone Photo</Label>
                <p className="text-xs text-muted-foreground">
                  JPEG, PNG, WebP. Maximum 10MB. One representative photo per zone.
                </p>
              </div>

              {isEditing && currentZone ? (
                <div className="space-y-3">
                  {currentPhotoPath && !photoPreviewError && (
                    <img
                      src={getZonePhotoUrl(currentZone.id)}
                      alt={`Zone ${currentZone.code} preview`}
                      className="h-28 w-full rounded-md object-cover"
                      onError={() => setPhotoPreviewError(true)}
                    />
                  )}

                  {currentPhotoPath && photoPreviewError && (
                    <div className="flex h-28 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                      Photo preview unavailable
                    </div>
                  )}

                  {!currentPhotoPath && (
                    <div className="flex h-20 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                      No photo uploaded yet
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handlePhotoSelection}
                    disabled={isPhotoBusy}
                  />

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isPhotoBusy}
                    >
                      {uploadPhotoMutation.isPending ? 'Uploading...' : currentPhotoPath ? 'Replace Photo' : 'Upload Photo'}
                    </Button>

                    {currentPhotoPath && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDeletePhoto}
                        disabled={isPhotoBusy}
                      >
                        {deletePhotoMutation.isPending ? 'Removing...' : 'Remove Photo'}
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Save the zone first, then you can upload a photo.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : isEditing ? 'Update Zone' : 'Create Zone'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
